import { useEffect, useState } from "react";
import { getGithubResource } from "./request";

interface DashboardStats {
  total_repos: number;
  total_contributors: number;
  total_commits: number;
  total_prs: number;
  new_repos_last_week: number;
  new_contributors_last_week: number;
  new_commits_last_week: number;
  new_prs_last_week: number;
  last_updated: string;
}

export interface TopContributorProps {
  top_contributors: {
    [username: string]: {
      avatarUrl: string;
      commits: number;
    };
  };
  last_updated: string;
}

export interface ContributorsCardProps {
  title: string;
  data: any;
  type: "List" | "Ring" | "ListCommits";
  isBelow800: boolean;
  info: any;
}

export interface recentCommits {
  recent_commits: Commit[];
  last_updated: string;
}

export interface Commit {
  author: {
    name: string;
    avatarUrl: string;
    user: {
      login: string;
    };
  };
  message: string;
  committedDate: string;
  oid: string;
  repository: {
    nameWithOwner: string;
  };
}

interface MonthlyCommitsData {
  monthly_commits: {
    [month: number]: number;
  };

  last_updated: string;
}

interface ActiveRepositoriesData {
  monthly_active_repos: {
    [month: number]: number;
  };

  last_updated: string;
}

interface ActiveContributorsData {
  active_contributors: string;
  last_updated: string;
}

export const useDashboardStats = () => {
  const [data, setData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { response } = await getGithubResource({ endpoint: "statistics" });
      if (response) {
        setData(response);
      }
      setLoading(false);
    })();
  }, []);

  return { data, loading };
};

export const useContributorStats = () => {
  const [contributorsData, setContributorsData] =
    useState<TopContributorProps | null>(null);
  const [contributorsLoading, setContributorsLoading] = useState(true);
  const [contributorsError, setContributorsError] = useState(null);

  useEffect(() => {
    (async () => {
      const { response, error } = await getGithubResource({
        endpoint: "top_contributors",
      });
      if (error) {
        setContributorsError(error);
        setContributorsData(null);
      }
      if (response) {
        setContributorsData(response);
        setContributorsError(null);
      }
      setContributorsLoading(false);
    })();
  }, []);

  return { contributorsData, contributorsLoading, contributorsError };
};

export const useMonthlyCommits = () => {
  const [chartData, setChartData] = useState<MonthlyCommitsData | null>(null);
  const [chartLoading, setChartLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMonthlyCommits = async () => {
      try {
        const { response, error } = await getGithubResource({
          endpoint: "monthly_commits",
        });

        if (error) {
          setError(error);
          setChartData(null);
        } else {
          setChartData(response);
          setError(null);
        }
      } catch (err) {
        setError("An error occurred while fetching the data");
      } finally {
        setChartLoading(false);
      }
    };

    fetchMonthlyCommits();
  }, []);

  return { chartData, chartLoading, error };
};

export const useActiveRepos = () => {
  const [activeRepoData, setActiveRepoData] =
    useState<ActiveRepositoriesData | null>(null);
  const [activeRepoLoading, setActiveRepoLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActiveRepo = async () => {
      try {
        const { response, error } = await getGithubResource({
          endpoint: "active_repos",
        });

        if (error) {
          setError(error);
          setActiveRepoData(null);
        } else {
          setActiveRepoData(response);
          setError(null);
        }
      } catch (err) {
        setError("An error occurred while fetching the data");
      } finally {
        setActiveRepoLoading(false);
      }
    };

    fetchActiveRepo();
  }, []);

  return { activeRepoData, activeRepoLoading, error };
};

export const useActiveContributors = () => {
  const [ActiveContributorsData, setActiveContributorsData] =
    useState<ActiveContributorsData | null>(null);
  const [ActiveContributorsLoading, setActiveContributorsLoading] =
    useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActiveContributors = async () => {
      try {
        const { response, error } = await getGithubResource({
          endpoint: "active_contributors",
        });

        if (error) {
          setError(error);
          setActiveContributorsData(null);
        } else {
          setActiveContributorsData(response);
          setError(null);
        }
      } catch (err) {
        setError("An error occurred while fetching the data");
      } finally {
        setActiveContributorsLoading(false);
      }
    };

    fetchActiveContributors();
  }, []);

  return { ActiveContributorsData, ActiveContributorsLoading, error };
};

export const useCommitHistory = () => {
  const [commits, setCommits] = useState<recentCommits | null>(null);
  const [commitsLoading, setCommitsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCommits = async () => {
      try {
        const { response, error } = await getGithubResource({
          endpoint: "recent_commits",
        });

        if (error) {
          setError(error);
          setCommits(null);
        } else {
          setCommits(response);
          setError(null);
        }
      } catch (err) {
        setError("An error occurred while fetching commit history.");
      } finally {
        setCommitsLoading(false);
      }
    };

    fetchCommits();
  }, []);

  return { commits, commitsLoading, error };
};
